import { agent } from "@shared/services/agent";
import { Period, WhereaboutsDayV2 } from '@features/whereabouts/models/movements.models';

export interface CreateVisitorBookingRequest {
  visitorBookingId?: number;
  date: string;
  visitingUserId?: string;
  visitorName: string;
  companyName: string;
  dueInHours: number;
  dueInMinutes: number;
  visitingOfficeId: number;
  amDeskId?: number;
  pmDeskId?: number;
  amParkingSpaceId?: number;
  pmParkingSpaceId?: number
}

export async function createVisitorBooking(request: CreateVisitorBookingRequest): Promise<any> {
  await agent.requests.post('/api/visitor-booking/new', request, true);
}

export async function cancelVisitorRequest(id: number): Promise<any> {
  await agent.requests.del(`/api/visitor-booking/${id}`);
}

export async function getVisitorBookings(officeId: number, dateString: string): Promise<VisitorBooking[]> {
  return await agent.requests.get(`/api/visitor-booking/${officeId}?date=${dateString}`, true);
}

export async function bookVisitorDeskRequest(visitorId: number, dateString: string, sideOfDay: Period, officeId?: number, deskId?: number, carParkingSpaceId?: number): Promise<VisitorBooking[]> {
  const request = {
    officeId: officeId,
    deskId: deskId,
    carParkId: carParkingSpaceId,
    visitorId: visitorId,
    dateString: dateString,
    sideOfDay: sideOfDay,
  }
  return await agent.requests.post(`/api/visitor-booking/book-desk`, request, true);
}

export async function cancelVisitorDeskRequest(visitorId: number, dateString: string): Promise<VisitorBooking[]> {
  const request = {
    visitorId: visitorId,
    dateString: dateString,
  }
  return await agent.requests.post(`/api/visitor-booking/cancel-desk`, request, true);
}

export async function signVisitorIn(bookingId: number): Promise<VisitorBooking> {
  return await agent.requests.post(`/api/visitor-booking/sign-in`, {visitorBookingId: bookingId}, true);
}

export async function signVisitorOut(bookingId: number): Promise<VisitorBooking> {
  return await agent.requests.post(`/api/visitor-booking/sign-out`, {visitorBookingId: bookingId}, true);
}

export async function getVisitorCount(dateString: string, officeId: number): Promise<VisitorCount> {
  return await agent.requests.get(`/api/visitor-booking/visitor-count/${officeId}/${dateString}`, true);
}

export interface VisitorBooking {
  id: number;
  visitingUserId: string;
  visitingUserName: string;
  visitingUserTeamName: string;
  officeId: number;
  date: string;
  visitorName: string;
  companyName: string;
  email: string;
  timeDueHours: number;
  timeDueMinutes: number;
  signInState: VisitorSignInState;

  amDeskId: number;
  pmDeskId: number;
  amDeskLabel: string;
  pmDeskLabel: string;

  amParkingSpaceId?: number;
  amParkingSpaceLabel?: string;
  pmParkingSpaceId?: number
  pmParkingSpaceLabel?: string;

  bookedInByUserId: string;
  bookedInByUserName: string;
  checkedInTime: any;

  bookedOutByUserId: string;
  bookedOutByUserName: string;
  signedOutTime: any;

  whereaboutsDay?: WhereaboutsDayV2;

  // Present only when the visitor was added to a meeting room booking. Times are UTC ISO strings.
  meetingRoomBookingId?: number;
  meetingRoomId?: string;
  meetingRoomName?: string;
  meetingStartDateTime?: string;
  meetingEndDateTime?: string;
}

export enum VisitorSignInState {
  NONE = 'NONE',
  CHECKED_IN = 'CHECKED_IN',
  CHECKED_OUT = 'CHECKED_OUT',
}

export interface RecentVisitor {
  visitorName: string;
  companyName: string;
  email: string;
}

export async function getRecentVisitors(): Promise<RecentVisitor[]> {
  return await agent.requests.get('/api/visitor-booking/recent', true);
}

export interface VisitorCount {
  count: number;
}
